/**
 * FavoritesView — redesigned Favorites tab UI
 *
 * Features:
 * - Filter pills: All Favorites | Wallpapers | Live | Ringtones | Icons (only show types present)
 * - Ringtones as horizontal row cards (full width, stacked vertically)
 * - Wallpapers / Live / Icons in a 2-column tall-card grid
 * - Floating "+" button to browse more
 * - Empty state when no favorites
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart,
  Home,
  Camera,
  Mail,
  MessageSquare,
  ImageOff,
  Music2,
  Layers,
  HeartOff,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { WallpapersResponse } from '@/lib/collections/wallpapers';
import { useRingtonePlayer } from '@/contexts/RingtonePlayerContext';
import { MiniPlayerBar } from '@/components/MiniPlayerBar';

// ─── Types ────────────────────────────────────────────────────────────────────

type MediaType = 'wallpaper' | 'live-wallpaper' | 'ringtone' | 'icon';
type FilterKey = 'all' | MediaType;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function resolveMediaType(w: WallpapersResponse): MediaType {
  const stored = (w as WallpapersResponse & { mediaType?: string }).mediaType;
  if (
    stored === 'wallpaper' ||
    stored === 'live-wallpaper' ||
    stored === 'ringtone' ||
    stored === 'icon'
  ) {
    return stored as MediaType;
  }
  const url = (w.imageUrl || '').toLowerCase().split('?')[0];
  if (/\.(mp4|webm|mov|m4v)$/.test(url)) return 'live-wallpaper';
  if (/\.(mp3|wav|ogg|m4a|aac)$/.test(url)) return 'ringtone';
  return 'wallpaper';
}

// ─── Icon pack tile definitions (fallback for icon packs without imageUrl) ───

const ICON_FALLBACK = [
  { bg: '#1c1c28', Icon: Home, color: '#a78bfa' },
  { bg: '#1c1c28', Icon: Camera, color: '#a78bfa' },
  { bg: '#1c1c28', Icon: Mail, color: '#a78bfa' },
  { bg: '#1c1c28', Icon: MessageSquare, color: '#a78bfa' },
];

function IconPackTilesSmall() {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        background: '#111118',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: '1fr 1fr',
        gap: 6,
        padding: 16,
        boxSizing: 'border-box',
      }}
    >
      {ICON_FALLBACK.map((item, i) => (
        <div
          key={i}
          style={{
            borderRadius: 12,
            background: item.bg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <item.Icon size={20} color={item.color} strokeWidth={1.8} />
        </div>
      ))}
    </div>
  );
}

// ─── Ringtone row card ────────────────────────────────────────────────────────

const RingtoneCard = React.memo(function RingtoneCard({
  wallpaper,
  isFavorited,
  isPurchased,
  index,
  playlist,
  onToggleFavorite,
  onSelect,
}: {
  wallpaper: WallpapersResponse;
  isFavorited: boolean;
  isPurchased: boolean;
  index: number;
  playlist: WallpapersResponse[];
  onToggleFavorite: (w: WallpapersResponse) => void;
  onSelect: (w: WallpapersResponse) => void;
}) {
  const { currentRingtone, isPlaying: globalPlaying, currentTime, duration: globalDuration, togglePlayPause, play, openFullScreen, setFavorited, setPurchased } = useRingtonePlayer();
  const isThisTrack = currentRingtone?.id === wallpaper.id;
  // Show active state whenever this track is the current track and audio is playing.
  const isPlaying = globalPlaying && isThisTrack;
  const progress = isThisTrack && globalDuration > 0
    ? (currentTime / globalDuration) * 100
    : 0;

  // Duration probe
  const [duration, setDuration] = useState(0);
  const probeRef = useRef<HTMLAudioElement>(null);

  const handleProbeLoaded = () => {
    const a = probeRef.current;
    if (a && isFinite(a.duration)) setDuration(a.duration);
  };

  const durationSecs = duration > 0 ? Math.round(duration) : null;
  const category = (wallpaper as WallpapersResponse & { category?: string }).category ?? 'Audio';
  const subtitle = durationSecs != null ? `${category} • ${durationSecs}s` : category;

  // Keep player context in sync when the currently-loaded track's purchased state
  // changes (e.g. after a purchase completes while this track is already loaded).
  useEffect(() => {
    if (currentRingtone?.id === wallpaper.id) {
      setPurchased(isPurchased);
    }
  }, [isPurchased, currentRingtone?.id, wallpaper.id, setPurchased]);

  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFavorited(isFavorited);
    setPurchased(isPurchased);
    togglePlayPause(wallpaper, playlist);
  };

  const handleBarClick = () => {
    setFavorited(isFavorited);
    setPurchased(isPurchased);
    play(wallpaper, playlist);
    openFullScreen();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ scale: 1.02, y: -2, transition: { type: 'spring', stiffness: 320, damping: 22 } }}
      whileTap={{ scale: 0.98, transition: { type: 'spring', stiffness: 320, damping: 22 } }}
      style={{ width: '100%', willChange: 'transform' }}
    >
      {/* Silent metadata probe */}
      <audio
        ref={probeRef}
        src={wallpaper.imageUrl ?? ''}
        preload="metadata"
        onLoadedMetadata={handleProbeLoaded}
        style={{ display: 'none' }}
      />

      <MiniPlayerBar
        title={wallpaper.title ?? 'Untitled'}
        subtitle={subtitle}
        isPlaying={isPlaying}
        progress={progress}
        onPlayPause={togglePlay}
        showNextButton={false}
        showDismissButton={false}
        onBarClick={handleBarClick}
        showFavoriteButton
        isFavorited={isFavorited}
        onToggleFavorite={(e) => { e.stopPropagation(); onToggleFavorite(wallpaper); }}
      />
    </motion.div>
  );
});

// ─── Visual card (wallpaper / live / icon) — Medium variant for Favorites ────
// 2-column grid, ~180px image height, title + category label BELOW image (outside card surface)

const VisualCard = React.memo(function VisualCard({
  wallpaper,
  mediaType,
  isFavorited,
  index,
  onToggleFavorite,
  onSelect,
}: {
  wallpaper: WallpapersResponse;
  mediaType: MediaType;
  isFavorited: boolean;
  index: number;
  onToggleFavorite: (w: WallpapersResponse) => void;
  onSelect: (w: WallpapersResponse) => void;
}) {
  const TYPE_LABEL: Record<MediaType, string> = {
    wallpaper: 'Wallpaper',
    'live-wallpaper': 'Live',
    ringtone: 'Ringtone',
    icon: 'Icon Pack',
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.32, delay: index * 0.055, ease: [0.22, 1, 0.36, 1] }}
        whileHover={{ scale: 1.03, y: -4, transition: { type: 'spring', stiffness: 320, damping: 22 } }}
        whileTap={{ scale: 0.97, transition: { type: 'spring', stiffness: 320, damping: 22 } }}
        onClick={() => onSelect(wallpaper)}
        onContextMenu={(e) => e.preventDefault()}
        style={{
          cursor: 'pointer',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
          touchAction: 'manipulation',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          minWidth: 0,
          overflow: 'hidden',
          willChange: 'transform',
        } as React.CSSProperties}
      >
      {/* Artwork area — fixed height, rounded card surface */}
      <div
        style={{
          position: 'relative',
          height: 180,
          borderRadius: 14,
          overflow: 'hidden',
          background: '#12111a',
          flexShrink: 0,
        }}
      >
        {mediaType === 'icon' ? (
          wallpaper.imageUrl ? (
            <img
              src={wallpaper.imageUrl}
              alt={wallpaper.title ?? 'Icon pack'}
              loading="lazy"
              decoding="async"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <IconPackTilesSmall />
          )
        ) : mediaType === 'live-wallpaper' ? (
          <video
            src={wallpaper.imageUrl}
            autoPlay
            loop
            muted
            playsInline
            onContextMenu={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
            controlsList="nodownload nofullscreen noremoteplayback"
            disablePictureInPicture
            disableRemotePlayback
            style={{
              width: '100%', height: '100%', objectFit: 'cover', display: 'block',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              WebkitTouchCallout: 'none',
            } as React.CSSProperties}
          />
        ) : wallpaper.imageUrl ? (
          <img
            src={wallpaper.imageUrl}
            alt={wallpaper.title ?? 'Wallpaper'}
            loading="lazy"
            decoding="async"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              background: 'linear-gradient(160deg, #1a0030 0%, #3d0073 50%, #6d28d9 100%)',
            }}
          />
        )}

        {/* Heart badge top-right — floats over the image */}
        <motion.button
          whileTap={{ scale: 0.88 }}
          onClick={(e) => { e.stopPropagation(); onToggleFavorite(wallpaper); }}
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            width: 30,
            height: 30,
            borderRadius: '50%',
            background: 'hsla(262, 83%, 66%, 0.22)',
            border: '1px solid hsla(262, 83%, 66%, 0.5)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 2,
          }}
        >
          <Heart
            size={13}
            strokeWidth={2.2}
            fill={isFavorited ? 'hsl(262, 83%, 66%)' : 'none'}
            color={isFavorited ? 'hsl(262, 83%, 66%)' : 'rgba(240,240,245,0.65)'}
          />
        </motion.button>
      </div>

      {/* Text block — OUTSIDE the card surface, below the image */}
      <div style={{ paddingLeft: 2, width: '100%', minWidth: 0, overflow: 'hidden' }}>
        <p
          style={{
            margin: 0,
            fontSize: 12,
            fontWeight: 700,
            color: '#f0f0f5',
            fontFamily: "'Syne', sans-serif",
            letterSpacing: '-0.01em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1.3,
            width: '100%',
            minWidth: 0,
          }}
        >
          {wallpaper.title ?? 'Untitled'}
        </p>
        <p
          style={{
            margin: '2px 0 0',
            fontSize: 10,
            fontWeight: 600,
            color: 'rgba(240,240,245,0.42)',
            fontFamily: "'Syne', sans-serif",
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
          }}
        >
          {TYPE_LABEL[mediaType]}
        </p>
      </div>
    </motion.div>
    </>
  );
});

// ─── Empty state config per filter ───────────────────────────────────────────

const FILTER_EMPTY_CONFIG: Record<FilterKey, {
  Icon: React.ElementType;
  heading: string;
  description: string;
  cta: string;
}> = {
  all: {
    Icon: HeartOff,
    heading: 'No favorites yet',
    description: 'Tap the heart on any wallpaper, ringtone, or icon pack to save it here.',
    cta: 'Discover wallpapers',
  },
  wallpaper: {
    Icon: ImageOff,
    heading: 'No favorite wallpapers',
    description: 'Browse the Explore page and heart the wallpapers you love.',
    cta: 'Explore wallpapers',
  },
  'live-wallpaper': {
    Icon: Layers,
    heading: 'No favorite live wallpapers',
    description: 'Explore animated live wallpapers and tap the heart to save them.',
    cta: 'Find live wallpapers',
  },
  ringtone: {
    Icon: Music2,
    heading: 'No favorite ringtones',
    description: 'Browse ringtones on the Explore page and save the ones that hit right.',
    cta: 'Explore ringtones',
  },
  icon: {
    Icon: Layers,
    heading: 'No favorite icon packs',
    description: 'Browse icon packs and heart the ones you want to keep handy.',
    cta: 'Explore icon packs',
  },
};

// ─── Global empty state (zero favorites) ─────────────────────────────────────

function EmptyState({ onBrowse }: { onBrowse: () => void }) {
  const config = FILTER_EMPTY_CONFIG.all;
  const { Icon } = config;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.38, ease: 'easeOut' }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '90px 32px 120px',
        gap: 20,
        textAlign: 'center',
      }}
    >
      {/* Glow circle with icon */}
      <div style={{ position: 'relative' }}>
        <div
          style={{
            position: 'absolute',
            inset: -16,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(167,139,250,0.18) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />
        <motion.div
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 3.4, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(139,92,246,0.18) 0%, rgba(109,40,217,0.1) 100%)',
            border: '1px solid rgba(139,92,246,0.35)',
            boxShadow: '0 0 24px rgba(139,92,246,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon size={30} color="hsl(262, 83%, 66%)" strokeWidth={1.5} />
        </motion.div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <p
          style={{
            color: '#f0f0f5',
            fontSize: 18,
            fontWeight: 800,
            margin: 0,
            fontFamily: "'Syne', sans-serif",
            letterSpacing: '-0.02em',
          }}
        >
          {config.heading}
        </p>
        <p
          style={{
            color: 'rgba(240,240,245,0.4)',
            fontSize: 13,
            margin: 0,
            maxWidth: 260,
            lineHeight: 1.55,
            fontFamily: "'Syne', sans-serif",
          }}
        >
          {config.description}
        </p>
      </div>

      <Button
        onClick={onBrowse}
        style={{
          background: '#a78bfa',
          color: '#fff',
          fontFamily: "'Syne', sans-serif",
          fontSize: 14,
          fontWeight: 700,
          border: 'none',
          boxShadow: '0 4px 20px rgba(167,139,250,0.35)',
          borderRadius: 9999,
          padding: '0 28px',
          height: 42,
        }}
      >
        {config.cta}
      </Button>
    </motion.div>
  );
}

// ─── Per-filter empty state (has items but none match current filter) ─────────

function FilterEmptyState({ filter, onBrowse }: { filter: FilterKey; onBrowse: () => void }) {
  const config = FILTER_EMPTY_CONFIG[filter] ?? FILTER_EMPTY_CONFIG.all;
  const { Icon } = config;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, ease: 'easeOut' }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '64px 32px 80px',
        gap: 14,
        textAlign: 'center',
      }}
    >
      <div style={{
        width: 64,
        height: 64,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, rgba(139,92,246,0.12) 0%, rgba(109,40,217,0.06) 100%)',
        border: '1.5px solid rgba(139,92,246,0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Icon size={28} color="rgba(167,139,250,0.55)" strokeWidth={1.5} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <p style={{
          color: '#f0f0f5',
          fontSize: 16,
          fontWeight: 700,
          margin: 0,
          fontFamily: "'Syne', sans-serif",
          letterSpacing: '-0.01em',
        }}>
          {config.heading}
        </p>
        <p style={{
          color: 'rgba(240,240,245,0.4)',
          fontSize: 13,
          margin: 0,
          maxWidth: 250,
          lineHeight: 1.55,
          fontFamily: "'Syne', sans-serif",
        }}>
          {config.description}
        </p>
      </div>

      <Button
        variant="outline"
        size="sm"
        onClick={onBrowse}
        style={{
          marginTop: 2,
          borderColor: 'rgba(139,92,246,0.3)',
          color: 'hsl(262, 83%, 75%)',
          fontFamily: "'Syne', sans-serif",
          fontSize: 13,
          fontWeight: 600,
          borderRadius: 9999,
        }}
      >
        {config.cta}
      </Button>
    </motion.div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface FavoritesViewProps {
  favorites: WallpapersResponse[];
  favoritedIds: Set<string>;
  purchasedIds: Set<string>;
  onToggleFavorite: (w: WallpapersResponse) => void;
  onSelectWallpaper: (w: WallpapersResponse) => void;
  onBrowse: () => void;
}

export function FavoritesView({
  favorites,
  favoritedIds,
  purchasedIds,
  onToggleFavorite,
  onSelectWallpaper,
  onBrowse,
}: FavoritesViewProps) {
  const [activeFilter, setActiveFilter] = useState<FilterKey>(() => {
    try {
      const stored = sessionStorage.getItem('seekscape-favorites-filter');
      if (stored && ['all', 'wallpaper', 'live-wallpaper', 'ringtone', 'icon'].includes(stored)) {
        return stored as FilterKey;
      }
    } catch {}
    return 'all';
  });

  // Persist filter selection across tab switches
  const handleSetActiveFilter = (filter: FilterKey) => {
    setActiveFilter(filter);
    try { sessionStorage.setItem('seekscape-favorites-filter', filter); } catch {}
  };

  // Annotate each item with its resolved media type
  const annotated = useMemo(
    () => favorites.map((w) => ({ w, type: resolveMediaType(w) })),
    [favorites]
  );

  // Determine which type pills to show (only if that type has items)
  const availableTypes = useMemo<MediaType[]>(() => {
    const seen = new Set<MediaType>();
    annotated.forEach(({ type }) => seen.add(type));
    return (['wallpaper', 'live-wallpaper', 'ringtone', 'icon'] as MediaType[]).filter((t) =>
      seen.has(t)
    );
  }, [annotated]);

  // Filter items based on active pill
  const filtered = useMemo(() => {
    if (activeFilter === 'all') return annotated;
    return annotated.filter(({ type }) => type === activeFilter);
  }, [annotated, activeFilter]);

  const FILTER_LABELS: Record<FilterKey, string> = {
    all: 'All Favorites',
    wallpaper: 'Wallpapers',
    'live-wallpaper': 'Live',
    ringtone: 'Ringtones',
    icon: 'Icons',
  };

  const pillFilters: FilterKey[] = ['all', ...availableTypes];

  if (favorites.length === 0) {
    return (
      <div style={{ position: 'relative' }}>
        <EmptyState onBrowse={onBrowse} />
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: 120, position: 'relative', overflow: 'hidden' }}>
      {/* ── Filter pills ────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '9px 14px 16px',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          flexWrap: 'nowrap',
        } as React.CSSProperties}
      >
        {pillFilters.map((filter) => {
          const isActive = activeFilter === filter;
          return (
            <motion.button
              key={filter}
              whileTap={{ scale: 0.92 }}
              onClick={() => handleSetActiveFilter(filter)}
              style={{
                height: 30,
                padding: '0 14px',
                borderRadius: 9999,
                border: isActive ? 'none' : '1px solid rgba(255,255,255,0.13)',
                background: isActive ? '#46f5e0' : 'transparent',
                color: isActive ? '#0a0a10' : 'rgba(240,240,245,0.5)',
                fontSize: 12,
                fontWeight: isActive ? 700 : 500,
                cursor: 'pointer',
                fontFamily: "'Syne', sans-serif",
                letterSpacing: isActive ? '-0.01em' : '0',
                flexShrink: 0,
                boxShadow: 'none',
                transition: 'background 0.18s ease, color 0.18s ease, border-color 0.18s ease',
                whiteSpace: 'nowrap',
              }}
            >
              {FILTER_LABELS[filter]}
            </motion.button>
          );
        })}
      </div>

      {/* ── Content ─────────────────────────────────────────────── */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeFilter}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.16 }}
        >
          {/* Merged list sorted by most-recently-favorited — ringtones are full-width rows,
              visual items are grouped into 2-column grid rows, all interleaved in sort order */}
          {filtered.length > 0 && (() => {
            // Full mixed playlist (all displayed items) — enables cross-type swipe navigation
            const mixedPlaylist = filtered.map(r => r.w);
            const rows: React.ReactNode[] = [];
            let i = 0;
            while (i < filtered.length) {
              const { w, type } = filtered[i];
              if (type === 'ringtone') {
                rows.push(
                  <div key={w.id} style={{ padding: '0 7px', marginBottom: 10 }}>
                    <RingtoneCard
                      wallpaper={w}
                      isFavorited={favoritedIds.has(w.id)}
                      isPurchased={purchasedIds.has(w.id)}
                      index={i}
                      playlist={mixedPlaylist}
                      onToggleFavorite={onToggleFavorite}
                      onSelect={onSelectWallpaper}
                    />
                  </div>
                );
                i++;
              } else {
                // Collect consecutive non-ringtone items for a 2-column grid row
                const groupStart = i;
                const group: typeof filtered = [];
                while (i < filtered.length && filtered[i].type !== 'ringtone') {
                  group.push(filtered[i]);
                  i++;
                }
                const total = group.length;
                rows.push(
                  <div
                    key={`visual-group-${groupStart}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 8,
                      padding: '0 7px',
                      marginBottom: i < filtered.length ? 10 : 0,
                    }}
                  >
                    {group.map(({ w: gw, type: gtype }, gi) => {
                      const isLastItem = gi === total - 1;
                      const shouldSpan = total === 1 || (total % 2 !== 0 && isLastItem);
                      const spanStyle: React.CSSProperties = shouldSpan
                        ? { gridColumn: '1 / -1', minWidth: 0, overflow: 'hidden' }
                        : { minWidth: 0, overflow: 'hidden' };
                      return (
                        <div key={gw.id} style={spanStyle}>
                          <VisualCard
                            wallpaper={gw}
                            mediaType={gtype}
                            isFavorited={favoritedIds.has(gw.id)}
                            index={groupStart + gi}
                            onToggleFavorite={onToggleFavorite}
                            onSelect={onSelectWallpaper}
                          />
                        </div>
                      );
                    })}
                  </div>
                );
              }
            }
            return <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>{rows}</div>;
          })()}

          {/* Empty for this filter */}
          {filtered.length === 0 && (
            <FilterEmptyState filter={activeFilter} onBrowse={onBrowse} />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export default FavoritesView;
